import { useEffect, useState, lazy, Suspense } from "react";
import ReactGA from "react-ga4";

import "../App.css";
import "../general.css";
import "../queries.css";

import Seo from "../components/SEO";
import { useIsMobile } from "../hook/useIsMobile";
import { useFacebookPageView } from "../hook/useFacebookPageView";
import SectionFadeIn from "../components/SectionFadeIn";
import PosterHero from "../section/mainsite/PosterHero";

const HeroSection = lazy(() => import("../section/mainsite/Hero"));
const CtaSection = lazy(() => import("../section/mainsite/CTA"));
const TheDrinkSection = lazy(() => import("../section/mainsite/TheDrink"));
const ScienceSection = lazy(() => import("../section/mainsite/Science"));
const TestemonialsSection = lazy(() => import("../section/mainsite/Use"));
const RecommendationSection = lazy(() => import("../section/mainsite/Recommendation"));
const QuestSection = lazy(() => import("../section/mainsite/Quest"));
const HealthSection = lazy(() => import("../section/mainsite/Health"));
const JoinSection = lazy(() => import("../section/mainsite/Join"));

const Skeleton = () => (
  <div style={{ width: "100%", height: "80vh", backgroundColor: "#111" }} />
);

function Index() {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [heroReady, setHeroReady] = useState(false);
  const isMobile = useIsMobile();

  

  useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: "/", title: "mainpage" });
  }, []);

  useFacebookPageView();

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const sections = [
{
  id: "section-hero-placeholder",
  component: <PosterHero loading={!heroReady} hidden={heroReady} />,
  alwaysVisible: true,
},
        {
      id: "section-hero",
      component: (
        <Suspense fallback={<Skeleton />}>
          <HeroSection onFirstVideoReady={() => setHeroReady(true)} />
        </Suspense>
      ),
    },
    {
      id: "cta",
      component: (
        <Suspense fallback={<Skeleton />}>
          <CtaSection />
        </Suspense>
      ),
    },
    {
      id: "thedrink",
      component: (
        <Suspense fallback={<Skeleton />}>
          <TheDrinkSection />
        </Suspense>
      ),
    },
    {
      id: "science",
      component: (
        <Suspense fallback={<Skeleton />}>
          <ScienceSection />
        </Suspense>
      ),
    },
    {
      id: "use",
      component: (
        <Suspense fallback={<Skeleton />}>
          <TestemonialsSection />
        </Suspense>
      ),
    },
    {
      id: "recommendation",
      component: (
        <Suspense fallback={<Skeleton />}>
          <RecommendationSection />
        </Suspense>
      ),
    },
    {
      id: "quest",
      component: (
        <Suspense fallback={<Skeleton />}>
          <QuestSection />
        </Suspense>
      ),
    },
    {
      id: "health",
      component: (
        <Suspense fallback={<Skeleton />}>
          <HealthSection />
        </Suspense>
      ),
    },
    {
      id: "join",
      component: (
        <Suspense fallback={<Skeleton />}>
          <JoinSection />
        </Suspense>
      ),
    },
  ];

  const orderedSections = isMobile
    ? [
        "section-hero-placeholder",
        "section-hero",
        "cta",
        "thedrink",
        "science",
        "use",
        "recommendation",
        "quest",
        "health",
        "join",
      ]
    : [
        "section-hero-placeholder",
        "section-hero",
        "thedrink",
        "cta",
        "science",
        "use",
        "recommendation",
        "quest",
        "health",
        "join",
      ];

  const getSectionById = (id: string) => {
    const section = sections.find((s) => s.id === id);
    if (!section) return null;
    return section;
  };

  return (
    <main>
      <Seo
        title="Rouse – For Better Sex & Hydration"
        description="Try Rouse: the science-backed drink that boosts hydration, energy, and intimacy."
        url="https://therouseco.com"
        keywords={["rouse", "intimacy drink", "hydration", "natural energy", "libido"]}
        uploadDate="2025-07-09"
        includeVideoSchema={true}
      />

      <nav className="main-nav">
        <ul className="main-nav-list sticky sticky-bottom">
          <li>
            <a className="main-nav-link" href="#thedrink">The Drink</a>
          </li>
          <li>
            <a className="main-nav-link" href="#quest"><s>Un</s>real Purpose</a>
          </li>
          <li>
            <a className="main-nav-link" href="#health">Don't Fuck With Your Health</a>
          </li>
          <li>
            <a className="main-nav-link" href="#join">Let's Get Intimate</a>
          </li>
        </ul>
      </nav>

      {orderedSections.map((id) => {
        const section = getSectionById(id);
        if (!section) return null;

        const visible = section.alwaysVisible || heroReady;

        return (
          <div
            key={id}
            style={{
              opacity: visible ? 1 : 0,
              pointerEvents: visible ? "auto" : "none",
              transition: "opacity 0.8s ease",
            }}
          >
            {isMobile ? section.component : <SectionFadeIn>{section.component}</SectionFadeIn>}
          </div>
        );
      })}
    </main>
  );
}

export default Index;
